import { WarningIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { SegmentsBar } from '../../components/composition-bar/segments-bar.tsx'
import { NoAnalysisCircle, SegmentsLabels } from '../../components/composition-bar/segments-labels.tsx'
import { ScorecardTotalDonut } from '../../components/scorecard-total-donut.tsx'
import { Table } from '../../components/table/table.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { Routes } from '../../routes.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { POSTURE_BAND_BAR_COLORS } from '../../utils/threat-model/scorecard-bands.css.ts'
import {
  BAND_LABELS,
  formatPosturePercent,
  POSTURE_BAND_ORDER,
  toPostureBand,
} from '../../utils/threat-model/scorecard-bands.ts'
import { CountDetailTooltip } from './count-detail-tooltip.tsx'
import type { DimensionPosture } from './org-scorecard-posture.ts'
import * as styles from './security-posture-page.css.ts'

/**
 * How the estate scores on each of the rubric's dimensions.
 *
 * **"Below adequate" is the column that earns this table.** A mean hides its distribution: a dimension
 * averaging 64% could be every repository sitting mediocre, or most of them fine with a handful in
 * freefall — and those need different responses while reading identically. The count says how many
 * repositories are actually in trouble, which is the number someone acts on.
 *
 * **"Scored" is a per-row denominator, not a page-level one.** A repository can be unscorable on one
 * dimension and fine on the rest, so rows genuinely differ, and "15 below adequate" means something
 * different out of 52 than out of 66.
 *
 * Confidence is the model's own hedge, reported rather than applied — weighting the mean by it would
 * produce a number no reader could reconstruct from the row in front of them.
 */

const centredHeader = (label: string) => () => <div className={styles.centredColumn}>{label}</div>

const columnHelper = createColumnHelper<DimensionPosture>()

/**
 * Built per render rather than declared once: the Mean posture donut states its own denominator in its
 * accessible label ("averaged across 52 of 66 repositories"), and that total is a property of the page,
 * not of the row. Memoised by the caller so the table is not handed a new column array each render.
 */
const buildColumns = (scorecardCount: number) => [
  columnHelper.accessor('displayName', {
    header: 'Dimension',
    cell: ({ row }) => row.original.displayName,
    meta: { width: '26%', className: typography.paragraph },
  }),
  columnHelper.accessor('meanScore', {
    header: centredHeader('Mean posture'),
    cell: ({ row }) => {
      const { meanScore } = row.original
      // Not scored is not zero: a rubric that could not be applied says nothing about the posture.
      if (meanScore === undefined) {
        return <span className={styles.freshnessValue}>Not scored</span>
      }

      return (
        <span className={styles.scoreCell}>
          {/* The same ring, at the same 40px, as the Risk column on the repositories page. The number
              stays inside it and the band keeps its word alongside, so the cell says the same two things
              it always did — the ring only adds the proportion at a glance. `plain` because the band word
              beside it already carries the colour; tinting both would say it twice. */}
          <ScorecardTotalDonut
            score={meanScore}
            scoredCount={row.original.scoredRepositoryCount}
            totalCount={scorecardCount}
            unitLabel="repositories"
            subject={row.original.displayName}
            size="table"
            valueTone="plain"
          />
          {/* Default text, not the band's own colour: the arc carries the band now, and tinting the word
              as well says it twice. The fixed-width slot is what keeps every ring at the same x — centred
              with a free-width label, a row reading "Fair" pushed its donut 15px right of a row reading
              "Adequate". */}
          <span className={styles.bandLabel}>{BAND_LABELS[toPostureBand(meanScore)]}</span>
        </span>
      )
    },
    meta: { width: '18%', className: styles.centredColumn },
  }),
  /**
   * How the estate falls across the bands on this dimension, and how much of it could be scored at all.
   *
   * Replaces the Scored and Below adequate columns it was prototyped beside. Those stated two numbers and
   * left the reader to compare them; the same twelve repositories below adequate mean something different
   * out of 58 than out of 70, and a mean of 66% can be a uniformly mediocre estate or a split one. The bar
   * shows which, the hover gives every band its count and the callout keeps the one number to act on.
   *
   * Every row divides the same whole — the estate's scorecard count — so the bars are comparable straight
   * down the page. A bar whose width were each row's own scored count would restate the trap above.
   */
  columnHelper.display({
    id: 'coverage',
    header: centredHeader(`Coverage of ${scorecardCount} repositories`),
    cell: ({ row }) => {
      const { displayName, scoredRepositoryCount, weakRepositoryCount, weakRepositories, bandCounts } = row.original
      const unscoredCount = Math.max(0, scorecardCount - scoredRepositoryCount)
      const share = (count: number) => (scorecardCount > 0 ? (count / scorecardCount) * 100 : 0)

      // Worst first, so the bar reads the way the bands rank and the eye meets the trouble at the left
      // edge of every row rather than hunting for it.
      const bands = POSTURE_BAND_ORDER.map(band => ({
        band,
        count: bandCounts[band],
        color: POSTURE_BAND_BAR_COLORS[band],
      }))

      return (
        <span className={styles.coverageCell}>
          <TooltipWithHover
            bare
            side="top"
            // Wide enough for all five bands on one line; the component caps at 300 by default, which
            // made the card overflow the popper it was measured into and sit off to one side of the bar.
            maxWidth={760}
            trigger={
              <span className={styles.coverageBarTrigger}>
                {/* Height and gap are inline because this branch's `SegmentsBar` offers `small` and
                    `tiny` only, and `tiny`'s 8px gap — sized for a 16px bar — was wider than some
                    segments here and broke the bar into detached chunks. ISS-8774 adds a 6px `thin`
                    size with a 3px gap; take that when the branches meet rather than adding a second. */}
                <SegmentsBar
                  size="tiny"
                  style={{ height: '8px', gap: '2px' }}
                  segments={[
                    ...bands.map(({ band, count, color }) => ({
                      ariaLabel: `${count} ${BAND_LABELS[band].toLowerCase()}`,
                      percentage: share(count),
                      color,
                    })),
                    // Dashed rather than a fifth fill: not scored is an absence, and a solid segment
                    // would read as one more band the estate was divided into.
                    {
                      ariaLabel: `${unscoredCount} not scored`,
                      percentage: share(unscoredCount),
                      color: 'transparent',
                      variant: 'dashed' as const,
                    },
                  ].filter(segment => segment.percentage > 0)}
                />
              </span>
            }
          >
            {/* No heading: the column header already says what the bands divide, and the band names carry
                their own meaning. `SegmentsLabels` drops to its plain card when the heading is omitted. */}
            <SegmentsLabels
              icon={false}
              labels={[
                ...bands.map(({ band, count, color }) => ({
                  name: BAND_LABELS[band],
                  percentage: share(count),
                  subLabel: `${count} ${pluralize(count, 'repository', 'repositories')}`,
                  color,
                })),
                unscoredCount > 0
                  ? {
                      name: 'Not scored',
                      percentage: share(unscoredCount),
                      subLabel: `${unscoredCount} ${pluralize(unscoredCount, 'repository', 'repositories')}`,
                      color: 'transparent',
                      icon: <NoAnalysisCircle />,
                    }
                  : undefined,
              ].filter(label => label !== undefined)}
            />
          </TooltipWithHover>

          {/* The one count kept in the open. The bar shows the shape; this is the number someone acts
              on, and it carries the list of which repositories they are — the tooltip that used to hang
              off the Below adequate column this cell replaced. */}
          <CountDetailTooltip
            heading={`Below adequate on ${displayName}`}
            triggerLabel={`${weakRepositoryCount} ${pluralize(weakRepositoryCount, 'repository', 'repositories')} below adequate on ${displayName}`}
            items={weakRepositories.map(repository => ({
              key: repository.repositoryId,
              name: repository.name,
              href: `${Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.repositoryId })}?tab=threat-model`,
              value: formatPosturePercent(repository.score),
            }))}
          >
            <span className={weakRepositoryCount > 0 ? styles.coverageBelowCount : styles.coverageLegend}>
              {weakRepositoryCount > 0 && (
                <WarningIcon className={styles.discloseWarning} size={14} weight="fill" aria-hidden />
              )}
              {weakRepositoryCount} below adequate
            </span>
          </CountDetailTooltip>
        </span>
      )
    },
    meta: { width: '22%', className: styles.centredColumn },
  }),
  columnHelper.accessor('meanConfidence', {
    header: centredHeader('Confidence'),
    cell: ({ row }) => (
      <span className={styles.numericCell}>
        {row.original.meanConfidence === undefined ? '—' : formatPosturePercent(row.original.meanConfidence)}
      </span>
    ),
    meta: { width: '12%', className: styles.centredColumn },
  }),
]

export const DimensionPostureTable: React.FC<{ dimensions: DimensionPosture[]; scorecardCount: number }> = ({
  dimensions,
  scorecardCount,
}) => {
  const columns = useMemo(() => buildColumns(scorecardCount) as ColumnDef<DimensionPosture>[], [scorecardCount])

  return (
    <Table
      tableName="Posture by dimension"
      data={dimensions}
      columns={columns}
      getRowId={row => row.dimension}
      emptyStateMessage={
        scorecardCount === 0
          ? 'No repository has a scorecard yet, so there is nothing to score.'
          : `No dimension was scorable across the ${scorecardCount} ${pluralize(scorecardCount, 'scorecard', 'scorecards')} that answered.`
      }
    />
  )
}
